import React, { useEffect, useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Bell, PackagePlus } from 'lucide-react';
import api from '@/lib/api';
import { useWs } from '@/lib/WsContext';
import { fmtDate } from '@/lib/api';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

export default function NotificationBell() {
  const { subscribe } = useWs();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);

  const fetchNotifs = useCallback(async () => {
    try {
      const { data } = await api.get('/notifications');
      setItems(data);
    } catch (e) { console.debug('[NotificationBell] fetchNotifs failed', e); }
  }, []);

  useEffect(() => { fetchNotifs(); }, [fetchNotifs]);

  useEffect(() => {
    const unsub = subscribe((msg) => {
      if (msg.event === 'notification') {
        setItems((prev) => [{ ...msg.data, read: false }, ...prev.filter((p) => p.id !== msg.data.id)]);
        toast(msg.data.title || 'Notifikasi baru', {
          description: msg.data.message,
          icon: <PackagePlus className="h-4 w-4" />,
        });
      }
    });
    return unsub;
  }, [subscribe]);

  const unread = items.filter((i) => !i.read).length;

  const handleClick = async (n) => {
    setOpen(false);
    try { await api.post(`/notifications/${n.id}/read`); } catch (e) { console.debug('[NotificationBell] markRead failed', e); }
    setItems((prev) => prev.map((p) => (p.id === n.id ? { ...p, read: true } : p)));
    if (n.type === 'new_product' && n.product_id) {
      navigate(`/owner/products?highlight=${n.product_id}`);
    }
  };

  const markAllRead = async () => {
    try { await api.post('/notifications/read-all'); } catch (e) { console.debug('[NotificationBell] markAllRead failed', e); }
    setItems((prev) => prev.map((p) => ({ ...p, read: true })));
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative" data-testid="owner-notification-bell-button" aria-label="Notifikasi">
          <Bell className="h-5 w-5" />
          <AnimatePresence>
            {unread > 0 && (
              <motion.span
                initial={{ scale: 0.6, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.6, opacity: 0 }}
                transition={{ type: 'spring', stiffness: 520, damping: 22 }}
                className="absolute -top-1 -right-1 min-w-5 h-5 px-1 rounded-full bg-destructive text-destructive-foreground text-[11px] flex items-center justify-center"
                data-testid="owner-notification-badge"
              >
                {unread > 99 ? '99+' : unread}
              </motion.span>
            )}
          </AnimatePresence>
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 p-0" data-testid="owner-notification-dropdown">
        <div className="flex items-center justify-between p-3 border-b">
          <div className="font-medium">Notifikasi</div>
          <Button variant="ghost" size="sm" onClick={markAllRead} disabled={unread === 0}>Tandai dibaca semua</Button>
        </div>
        <ScrollArea className="h-80">
          {items.length === 0 ? (
            <div className="p-6 text-center text-sm text-muted-foreground">Belum ada notifikasi</div>
          ) : (
            <ul className="divide-y">
              {items.map((n) => (
                <li key={n.id}>
                  <button onClick={() => handleClick(n)} className="w-full text-left p-3 hover:bg-accent transition-colors">
                    <div className="flex items-start gap-2">
                      <div className={`mt-1 h-2 w-2 rounded-full ${n.read ? 'bg-muted' : 'bg-primary'}`} />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">{n.title}</div>
                        <div className="text-xs text-muted-foreground line-clamp-2">{n.message}</div>
                        <div className="text-[11px] text-muted-foreground mt-1">{fmtDate(n.created_at)}</div>
                      </div>
                      {n.type === 'new_product' && <Badge variant="secondary" className="text-[10px]">Barang Baru</Badge>}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}
